import { z } from 'zod';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { PhoneNumberSchema } from './auth.schemas.js';

export const OperatingHoursInputSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  openTime: z.string().regex(/^\d{2}:\d{2}$/),
  closeTime: z.string().regex(/^\d{2}:\d{2}$/),
  isClosed: z.boolean().default(false),
});

export const UpsertOperatingHoursInputSchema = z.object({
  hours: z.array(OperatingHoursInputSchema).max(7),
});

export type OperatingHoursInput = z.infer<typeof OperatingHoursInputSchema>;
export type UpsertOperatingHoursInput = z.infer<typeof UpsertOperatingHoursInputSchema>;

const OperatingHoursSchema = OperatingHoursInputSchema;
const LockConfigSchema = z.record(z.unknown()).default({});
const LockAlertPhoneNumberSchema = PhoneNumberSchema.transform(
  (value) => parsePhoneNumberFromString(value)?.number ?? value,
);

export const GymContentTranslationsSchema = z
  .record(
    z
      .string()
      .regex(/^[a-zA-Z]{2,3}(?:-[a-zA-Z0-9]{2,8})*$/)
      .max(35),
    z
      .object({
        equipmentDescription: z.string().max(10000).nullable().optional(),
        directionsDescription: z.string().max(10000).nullable().optional(),
      })
      .strict(),
  )
  .refine((value) => Object.keys(value).length <= 50, 'Too many locales');

export const CreateGymInputSchema = z.object({
  name: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z0-9-]+$/),
  addressLine1: z.string().min(1),
  addressLine2: z.string().optional(),
  city: z.string().min(1),
  postcode: z.string().min(1),
  country: z.string().length(2).default('CZ'),
  contactEmail: z.string().email().nullable().optional(),
  contactPhone: z.string().max(50).nullable().optional(),
  contentTranslations: GymContentTranslationsSchema.optional(),
  equipmentDescription: z.string().max(10000).nullable().optional(),
  directionsDescription: z.string().max(10000).nullable().optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  timezone: z.string().default('Europe/Prague'),
  capacity: z.number().int().min(1).default(1),
  slotDurationMinutes: z.number().int().min(30).max(480).default(60),
  cancellationDeadlineHours: z.number().int().min(0).max(720).default(12),
  accessCodeValidBeforeMin: z.number().int().min(0).max(1440).default(3),
  accessCodeValidAfterMin: z.number().int().min(0).max(1440).default(3),
  slotBufferMinutes: z.number().int().min(0).max(240).default(5),
  lockConfig: LockConfigSchema.optional(),
  lockAlertPhoneNumbers: z
    .array(LockAlertPhoneNumberSchema)
    .max(10)
    .transform((numbers) => [...new Set(numbers)])
    .default([]),
  operatingHours: z.array(OperatingHoursSchema).optional(),
});

export const UpdateGymInputSchema = CreateGymInputSchema.partial().extend({
  tenantId: z.string().uuid().optional(),
});

export type CreateGymInput = z.infer<typeof CreateGymInputSchema>;
export type UpdateGymInput = z.infer<typeof UpdateGymInputSchema>;
